import { call, put } from 'redux-saga/effects';
import { getData, postData } from '../shared/fetchApi';
import {
    checkHasStoffstrombilanzenFailure,
    checkHasStoffstrombilanzenSuccess,
    deleteStoffstrombilanzFailure,
    deleteStoffstrombilanzSuccess,
    infobaseAvailable,
    infobaseNotAvailable,
    loadStoffstrombilanzenFailure,
    loadStoffstrombilanzenSuccess,
    sendStoffstrombilanzRequestFailure,
    sendStoffstrombilanzRequestSuccess,
    StoffstrombilanzPage,
    StoffstrombilanzRequest,
} from '../store/Stoffstrombilanz.store';
import { PayloadAction } from '@reduxjs/toolkit';

function* processCreateRequest(request: StoffstrombilanzRequest): Generator<unknown, StoffstrombilanzRequest, StoffstrombilanzRequest> {
    try {
        return yield call(postData, 'stoffstrombilanz', request, 'POST');
    } catch (error) {
        console.error('Error in processMessage:', error);
        throw error;
    }
}

export function* sendStoffstrombilanzRequestToBackend(
    action: PayloadAction<StoffstrombilanzRequest>
): Generator<unknown, void, StoffstrombilanzRequest> {
    try {
        console.log('Handling sendMessage:', action.payload);

        const reply: StoffstrombilanzRequest = yield call(processCreateRequest, action.payload);

        console.log('Message processed successfully, reply:', reply);

        yield put({ type: sendStoffstrombilanzRequestSuccess.type, payload: reply });
    } catch (error) {
        console.error('Error while sending message:', error);

        const response = (error as { response?: { status?: number; data?: { code?: string; message?: string } } } | undefined)?.response;
        if (response?.status === 422 && response?.data?.code === 'INFOBASE_EMPTY_RESPONSE' && response.data.message) {
            yield put(sendStoffstrombilanzRequestFailure(response.data.message));
            return;
        }
        // Fallback: generische Fehlermeldung
        yield put(
            sendStoffstrombilanzRequestFailure(
                'Die Erstellung der Stoffstrombilanzen ist fehlgeschlagen. Bitte versuchen Sie es später erneut.'
            )
        );
    }
}

function* processGetStoffstrombilanzenRequest(request: {
    number: number;
    size: number;
    sort: string;
    partnerNummer?: string;
}): Generator<unknown, StoffstrombilanzPage, StoffstrombilanzPage> {
    try {
        let url = `stoffstrombilanz/all?sort=${request.sort}&page=${request.number}&size=${request.size}`;
        if (request.partnerNummer) url += `&partnerNummer=${request.partnerNummer}`;
        return yield call(getData, url);
    } catch (error) {
        // console.error('Error in processMessage:', error);
        throw new Error('Failed to send message');
    }
}

export function* getStoffstrombilanzenSaga(
    action: PayloadAction<{
        number: number;
        size: number;
        sort: string;
        partnerNummer?: string;
    }>
): Generator<unknown, void, StoffstrombilanzPage> {
    try {
        const reply: StoffstrombilanzPage = yield call(processGetStoffstrombilanzenRequest, action.payload);

        // console.log('Message processed successfully, reply:', reply);

        yield put({ type: loadStoffstrombilanzenSuccess.type, payload: reply });
    } catch (error) {
        // console.error('Error while sending message:', error);

        yield put(loadStoffstrombilanzenFailure());
    }
}

function* processDeleteRequest(id: string): Generator<unknown, void, void> {
    try {
        yield call(postData, `stoffstrombilanz/${id}`, {}, 'DELETE'); // Assuming DELETE uses similar structure to POST
    } catch (error) {
        console.error(`Error while deleting Stoffstrombilanz with ID ${id}:`, error);
        throw new Error('Failed to delete Stoffstrombilanz');
    }
}

export function* deleteStoffstrombilanzSaga(action: PayloadAction<string>): Generator<unknown, void, void> {
    try {
        console.log('Handling delete request for ID:', action.payload);

        // Call the process function to execute delete
        yield call(processDeleteRequest, action.payload);

        console.log('Stoffstrombilanz deleted successfully, ID:', action.payload);

        // Dispatch success action
        yield put({
            type: deleteStoffstrombilanzSuccess.type,
            payload: action.payload,
        });
    } catch (error) {
        console.error('Error while deleting Stoffstrombilanz:', error);

        // Dispatch failure action
        yield put(deleteStoffstrombilanzFailure());
    }
}

async function checkServiceStatusAPI(): Promise<boolean> {
    const infobaseUrl = 'stoffstrombilanz/infobase/status';

    const response = await getData<boolean>(infobaseUrl);

    return response;
}

// Redux-Saga to check and handle web service availability

export function* checkInfobaseAvailableSaga(): Generator<unknown, void, boolean> {
    try {
        console.log('Checking infobase availability');
        const isAvailable: boolean = yield call(checkServiceStatusAPI);

        if (isAvailable) {
            yield put(infobaseAvailable());
        } else {
            yield put(infobaseNotAvailable());
        }
    } catch (error) {
        yield put(infobaseNotAvailable());
    }
}

async function checkHasStoffstrombilanzenAPI(): Promise<boolean> {
    const url = 'stoffstrombilanz/has-any';
    const response = await getData<any>(url);
    console.log('API response for stoffstrombilanz/has-any:', response, 'type:', typeof response, 'JSON:', JSON.stringify(response));

    // Handle different response formats
    if (typeof response === 'boolean') {
        return response;
    }
    if (typeof response === 'object' && response !== null) {
        // Check if response has a property containing the boolean
        if ('hasStoffstrombilanzen' in response) {
            return response.hasStoffstrombilanzen === true;
        }
        if ('hasAny' in response) {
            return response.hasAny === true;
        }
    }
    // Default to false if we can't parse the response
    console.warn('Unexpected response format from stoffstrombilanz/has-any:', response);
    return false;
}

export function* checkHasStoffstrombilanzenSaga(): Generator<unknown, void, boolean> {
    try {
        console.log('Checking if user has stoffstrombilanzen');
        const hasStoffstrombilanzen: boolean = yield call(checkHasStoffstrombilanzenAPI);
        console.log('Has stoffstrombilanzen:', hasStoffstrombilanzen);
        yield put(checkHasStoffstrombilanzenSuccess(hasStoffstrombilanzen));
    } catch (error) {
        console.error('Error checking stoffstrombilanzen:', error);
        yield put(checkHasStoffstrombilanzenFailure());
    }
}
